import React, { useState } from 'react'
import { apiGet } from '../../../generic/Api_Functions'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import { DefaultAccordion } from '../../../generic/DefaultAccordion'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import PartHistoryHeader from './PartHistoryHeader'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import { Chip, Stack, Tooltip, Typography } from '@mui/material'

// Import typedefs from another file
/**
 * @typedef {import('../../../generic/utilities/objectStructures/partWindow/part').PartHistoryHeader} PartHistoryHeader PartHistoryHeader object
 */

/**
 *
 * @param {Object} props general props object
 * @param {BigInt} props.partId Part object id number
 * @returns
 */
const PartHistory = (props) => {
  const { t_ } = useSmartTranslation()

  /** @type {[PartHistoryHeader, React.Dispatch<PartHistoryHeader>]} */
  const [part, setPart] = useState(null)

  useSmartInterval(
    async () => {
      if (props.partId === null) {
        return null
      }
      return await apiGet('parts/' + props.partId)
    }, 1000,
    (data) => {
      if (data != null) {
        setPart(data)
      }
    }
  )

  const getJobTableData = async (pagination) => {
    if (props.partId != null) {
      const jobData = await apiGet('partWorkflowHistory/' + props.partId, pagination)
      return jobData
    }
  }

  const getMoveTableData = async (pagination) => {
    if (props.partId != null) {
      const moveData = await apiGet('partFixtureMoveHistory/' + props.partId, pagination)
      return moveData
    }
  }

  // SAT #4009 (sun) / #3141: which raw material instances this part consumed, at which job and asset, how
  // much and when. An instance only turns PROCESSED once it is exhausted, so its status alone cannot tell.
  const getRawMaterialTableData = async (pagination) => {
    if (props.partId != null) {
      return await apiGet('partRawMaterialConsumption/' + props.partId, pagination)
    }
  }

  const columnsJobs = [
    {
      name: t_('Operation Result'),
      field: 'operationResult',
      render: (opResult) => {
        return (
          <>
            <span>{t_(opResult.operationResult)}</span>
          </>
        )
      }
    },
    {
      name: t_('Result description'),
      field: 'operationResultDescription'
    },
    {
      name: t_('Job'),
      field: 'technology',
      render: (job) => {
        return (
          <>
            <span>{t_(job.technology)}</span>
          </>
        )
      }
    },
    {
      name: t_('Asset'),
      field: 'device.name'
    },
    {
      name: t_('Program'),
      field: 'programs.name',
      render: (row) => {
        if (row.programs.length === 0) {
          return ''
        }
        if (row.programs.length === 1) {
          return row.programs[0]
        }
        return <Tooltip title={<Stack direction='column' gap={1}>{row.programs.filter((item, index) => index !== 0).map((item) => { return <Typography key={item}>{item}</Typography> })}</Stack>}><Stack direction='row' justifyContent='center'>{row.programs[0]}<Chip label={'+' + (row.programs.length - 1)} /></Stack></Tooltip>
      }
    },
    {
      name: t_('Date start'),
      field: 'dateStart',
      type: 'dateTimePicker'
    },
    {
      name: t_('Date end'),
      field: 'dateEnd',
      type: 'dateTimePicker'
    },
    {
      name: t_('Duration'),
      field: 'duration',
      sortable: false
    }
  ]

  const columnsMovement = [
    {
      name: t_('From'),
      field: 'deviceFrom',
      sortable: false
    },
    {
      name: t_('To'),
      field: 'deviceTo',
      sortable: false
    },
    {
      name: t_('Handler'),
      field: 'handler'
    },
    {
      name: t_('Date start'),
      field: 'dateStart',
      type: 'dateTimePicker'
    },
    {
      name: t_('Date end'),
      field: 'dateEnd',
      type: 'dateTimePicker'
    },
    {
      name: t_('Duration'),
      field: 'duration',
      sortable: false
    }
  ]

  // The second-unit columns are derived in Sun (quantity x the instance's size), so they are shown but can be
  // neither searched nor sorted there.
  const columnsRawMaterials = [
    {
      name: t_('Instance'),
      field: 'rawMaterial.code'
    },
    {
      name: t_('Raw Material Reference'),
      field: 'rawMaterialReference.code'
    },
    {
      name: t_('Batch'),
      field: 'rawMaterial.batch'
    },
    {
      name: t_('Quantity'),
      field: 'quantity',
      numeric: true
    },
    {
      name: t_('Quantity second unit'),
      field: 'secondUnitQuantity',
      numeric: true,
      sortable: false,
      search: false
    },
    {
      name: t_('Unit'),
      field: 'secondUnit',
      sortable: false,
      search: false
    },
    {
      name: t_('Job'),
      field: 'job',
      render: (row) => <span>{row.job ? t_(row.job) : ''}</span>
    },
    {
      name: t_('Asset'),
      field: 'deviceName'
    },
    {
      name: t_('Date'),
      field: 'date',
      type: 'dateTimePicker'
    }
  ]

  const sortOptions = {
    default: {
      field: 'dateEnd',
      direction: -1
    }
  }

  const rawMaterialSortOptions = {
    default: {
      field: 'date',
      direction: -1
    }
  }
  return (
    <div style={{ padding: '2rem' }}>{part !== null
      ? (
        <><PartHistoryHeader part={part} />
          <DefaultAccordion title={t_('JOBS')}>
            <SmartMateriaUITable
              tableTuneUniqueKey='partJobs'
              columns={columnsJobs}
              dataFetch={getJobTableData}
              pageable
              sortOptions={sortOptions}
              sticky={false}
            />
          </DefaultAccordion>
          <DefaultAccordion title={t_('MOVES')}>
            <SmartMateriaUITable
              tableTuneUniqueKey='partMoves'
              columns={columnsMovement}
              dataFetch={getMoveTableData}
              pageable
              sortOptions={sortOptions}
              sticky={false}
            />
          </DefaultAccordion>
          <DefaultAccordion title={t_('Raw materials').toUpperCase()}>
            <SmartMateriaUITable
              tableTuneUniqueKey='partRawMaterials'
              columns={columnsRawMaterials}
              dataFetch={getRawMaterialTableData}
              pageable
              sortOptions={rawMaterialSortOptions}
              sticky={false}
            />
          </DefaultAccordion>
        </>
        )
      : null}
    </div>
  )
}

export default PartHistory
